/**
 * Отображение значений типоспецифичных свойств.
 *
 * Как и формы, выводится из реестра: способ показа определяется `kind`
 * свойства, а не списком полей в коде экрана.
 */

import { ExternalLinkIcon } from 'lucide-react';
import {
  formatMoney,
  type Currency,
  type ItemType,
  type PropertyDefinition,
  type UserDto,
} from '@project-os/contracts';
import { getDetailProperties, getJsonProperties } from '@project-os/contracts';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

function enumLabel(property: PropertyDefinition, value: unknown): string {
  const option = property.options?.find((item) => item.value === value);
  return option?.labelRu ?? String(value);
}

export function PropertyValue({
  property,
  value,
  currency,
  members = [],
  className,
}: {
  property: PropertyDefinition;
  value: unknown;
  currency?: Currency;
  members?: UserDto[];
  className?: string;
}) {
  if (value === null || value === undefined || value === '') {
    return <span className={cn('text-muted-foreground/70', className)}>—</span>;
  }

  switch (property.kind) {
    case 'url':
      return (
        <a
          href={String(value)}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(
            'inline-flex max-w-full items-center gap-1 text-primary underline-offset-4 hover:underline',
            className,
          )}
        >
          <span className="truncate">{String(value)}</span>
          <ExternalLinkIcon className="size-3 shrink-0" aria-hidden />
        </a>
      );

    case 'email':
      return (
        <a href={`mailto:${String(value)}`} className={cn('hover:underline', className)}>
          {String(value)}
        </a>
      );

    case 'money':
      return (
        <span className={cn('tabular-nums', className)}>
          {formatMoney({ amountMinor: Number(value), currency: currency ?? 'EUR' })}
        </span>
      );

    case 'currency':
      return <span className={className}>{String(value)}</span>;

    case 'date':
      return <span className={className}>{formatDate(String(value))}</span>;

    case 'datetime':
      return <span className={className}>{formatDateTime(String(value))}</span>;

    case 'enum':
      return <span className={className}>{enumLabel(property, value)}</span>;

    case 'boolean':
      return <span className={className}>{value ? 'Да' : 'Нет'}</span>;

    case 'integer':
    case 'decimal':
      return (
        <span className={cn('tabular-nums', className)}>
          {new Intl.NumberFormat('ru-RU').format(Number(value))}
        </span>
      );

    case 'userRefList': {
      const ids = Array.isArray(value) ? (value as string[]) : [];
      if (ids.length === 0) return <span className="text-muted-foreground/70">—</span>;
      const names = ids.map(
        (id) => members.find((member) => member.id === id)?.name ?? 'Неизвестный участник',
      );
      return <span className={className}>{names.join(', ')}</span>;
    }

    case 'text':
      return <span className={cn('whitespace-pre-wrap', className)}>{String(value)}</span>;

    case 'string':
    default:
      return <span className={className}>{String(value)}</span>;
  }
}

/**
 * Полный список свойств типа в виде описания.
 * Пустые необязательные поля скрываются, чтобы карточка не превращалась
 * в перечень прочерков.
 */
export function PropertyList({
  type,
  properties,
  detail,
  members,
  showEmpty = false,
}: {
  type: ItemType;
  properties: Record<string, unknown>;
  detail: Record<string, unknown> | null;
  members?: UserDto[];
  showEmpty?: boolean;
}) {
  const jsonProperties = getJsonProperties(type);
  const detailProperties = detail ? getDetailProperties(type) : [];
  const currency = (detail?.currency as Currency | undefined) ?? 'EUR';

  const rows = [
    ...jsonProperties.map((property) => ({ property, value: properties[property.key] })),
    ...detailProperties.map((property) => ({ property, value: detail?.[property.key] })),
  ].filter(
    ({ property, value }) =>
      showEmpty || property.required || (value !== null && value !== undefined && value !== ''),
  );

  if (rows.length === 0) return null;

  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
      {rows.map(({ property, value }) => (
        <div key={property.key} className="contents">
          <dt className="text-xs text-muted-foreground sm:pt-0.5">{property.labelRu}</dt>
          <dd className="min-w-0 text-sm">
            <PropertyValue
              property={property}
              value={value}
              currency={currency}
              members={members}
            />
          </dd>
        </div>
      ))}
    </dl>
  );
}
